import { useAppSelector } from 'hooks/redux';
import React, { useEffect } from 'react';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';

import ExpandableCard from 'components/ExpandableCard';
import InfoTooltipIcon from 'components/InfoTooltipIcon';
import CategoryField from 'components/QuickenControls/CategoryField';
import UsageWithCategory from 'components/UsageWithCategory';
import type { CategoryTargetProps } from 'containers/Settings/ChildPages/RulesSimplifi/targetConditions/types';

import { categoriesSelectors } from 'app-components/flux/categories';
import { chartOfAccountsTypes } from 'app-components/flux/chart-of-accounts';
import type { ChartOfAccountProps } from 'app-components/flux/chart-of-accounts/chartOfAccountsTypes';
import { entitlementsSelectors } from 'app-components/flux/entitlements';
import { useField } from 'app-components/flux/transaction-rules/transactionRulesFormHooks';

import useStyles from '../styles';

function CategoryTarget(props: CategoryTargetProps) {
  const { classes } = useStyles();
  const businessEnabled = useAppSelector(entitlementsSelectors.getBusinessEnabled);
  const { value: coa, onValueChange: onCoaValueChange, error: coaError } = useField('coaTarget');
  const {
    value: businessCoa,
    onValueChange: onBusinessCoaValueChange,
    error: businessCoaError,
  } = useField('businessCoaTarget');
  const { value: bizId, onValueChange: onBizValueChange } = useField('businessTargetId');
  const { value: enabled } = useField('isEnabledCoaTarget');
  const { value: useDefaultAccount, onValueChange: onUseDefaultAccountChange } = useField('useDefaultAccountUsage');
  const isDowngradedUser =
    useAppSelector((state) => categoriesSelectors.getBusinessCoaIds(state)?.length) && !businessEnabled;
  const { isEditing } = props;
  let showBuisnessCategories = false;
  if (businessEnabled || (isDowngradedUser && isEditing)) {
    showBuisnessCategories = true;
  }
  const toolTipText =
    'We recommend keeping this box checked. Unchecking will apply the single usage you choose to all transactions that match your rule.';
  const helpText = useDefaultAccount
    ? 'Recommended: choose the relevant categories for business and personal accounts. They will automatically be applied when transactions are downloaded.'
    : "Choose a single category and a single usage. This will apply an exact usage and category regardless of the target transaction's account usage.";

  useEffect(() => {
    if (coa && !enabled) {
      onCoaValueChange(undefined);
    }
  }, [coa, enabled, onCoaValueChange, onUseDefaultAccountChange]);

  return (
    <Box className={classes.cardContent}>
      {showBuisnessCategories && (
        <Box className={classes.row} sx={{ alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Checkbox
              sx={{ py: 0, px: 0 }}
              id='apply-rule-to-existing'
              checked={useDefaultAccount}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                onUseDefaultAccountChange(!!event.target.checked);
              }}
            />
            <Typography
              variant='paragraphRegular'
              className={useDefaultAccount ? classes.selected : ''}
              sx={{ pl: 1, pr: 0, maxWidth: '260px', width: '100%' }}
            >
              Categorize for business and personal accounts separately
            </Typography>
            <InfoTooltipIcon
              className={classes.infoIcon}
              tooltipPlacement='right'
              tooltipHTML={<span>{toolTipText}</span>}
            />
          </Box>
          <Divider orientation='vertical' variant='middle' flexItem data-orientation='vertical'></Divider>
          <Divider orientation='horizontal' data-orientation='horizontal'></Divider>
          <Box sx={{ display: 'flex', flex: 1, pl: 1.5 }}>
            <Typography variant='captionRegular'>{helpText}</Typography>
          </Box>
        </Box>
      )}

      {useDefaultAccount && (
        <>
          <Box className={classes.row}>
            <Typography variant='paragraphRegular' sx={{ width: '163px', flex: 'none' }}>
              For transactions in business accounts:
            </Typography>

            <CategoryField
              variant='bodyRegular'
              id={'business-category'}
              editable
              createEnabled
              filterFn={(coa: ChartOfAccountProps) => {
                return coa.type === chartOfAccountsTypes.CoaTypeEnum.UNCATEGORIZED || coa.id !== 'PERSONAL';
              }}
              numPopularCats={0}
              value={businessCoa}
              onChange={(coa: { toJS: () => ChartOfAccountProps }) => onBusinessCoaValueChange(coa.toJS())}
              disableUnderline
              textFieldProps={{ classes: { root: classes.textField } }}
              inputStyle={classes.inputStyle}
              width={'100%'}
              allowBlank
              placeholder='Business category'
              isRequired={businessCoaError}
            />
          </Box>

          <Box className={classes.row}>
            <Typography variant='paragraphRegular' sx={{ width: '163px', flex: 'none' }}>
              For transactions in personal accounts:
            </Typography>

            <CategoryField
              variant='bodyRegular'
              id={'personal-category'}
              editable
              createEnabled
              filterFn={(coa: ChartOfAccountProps) => {
                return coa.type === chartOfAccountsTypes.CoaTypeEnum.UNCATEGORIZED || coa.id !== 'BUSINESS';
              }}
              numPopularCats={0}
              value={coa}
              onChange={(coa: { toJS: () => ChartOfAccountProps }) => onCoaValueChange(coa.toJS())}
              disableUnderline
              textFieldProps={{ classes: { root: classes.textField } }}
              inputStyle={classes.inputStyle}
              width={'100%'}
              allowBlank
              placeholder='Personal category'
              isRequired={coaError}
            />
          </Box>
        </>
      )}

      {!useDefaultAccount && (
        <UsageWithCategory
          categoryProps={{
            value: coa,
            margin: 'normal',
            textFieldVariant: 'filled',
            //variant: 'body1',
            editable: true,
            name: 'txn-details',
            id: 'rule-coa-target',
            classes: { root: classes.categoryTargetContainer },
            createEnabled: true,
            allowBlank: true,
            placeholder: 'Category',
            isRequired: coaError,
            numPopularCats: isDowngradedUser ? 0 : 5,
            filterFn: (coa: ChartOfAccountProps) => {
              if (isDowngradedUser && !isEditing) {
                // Do not show Business category option to downgraded user in create case
                return coa.type === chartOfAccountsTypes.CoaTypeEnum.UNCATEGORIZED || coa.id !== 'BUSINESS';
              } else {
                return true;
              }
            },
          }}
          onChange={({ usage, coa }) => {
            onCoaValueChange(coa);
            onBizValueChange(usage === 'PERSONAL' ? undefined : usage);
          }}
          usageProps={{
            value: bizId,
            className: classes.usageTargetContainer,
          }}
        />
      )}
    </Box>
  );
}

export function CategoryTargetCard(props: any) {
  const { classes, theme } = useStyles();
  const { value, onValueChange } = useField('isEnabledCoaTarget');

  const toggleEnabled = () => {
    onValueChange(!value);
  };

  return (
    <ExpandableCard
      id='rule-category-target'
      className={classes.card}
      title='Update Category'
      cardHeaderProps={{ padding: `${theme.spacing(1.5)} ${theme.spacing(2)}` }}
      titleProps={{ variant: 'subtitle2' }}
      expandIcon={<Checkbox id='rule-category-target-enabled' checked={value} onClick={toggleEnabled}></Checkbox>}
      isExpanded={value || !!props.hideToggle}
      hideToggle={!!props.hideToggle}
    >
      <CategoryTarget isEditing={props.isEditing} />
    </ExpandableCard>
  );
}
